import React from "react";
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { HtmlTooltip, IconContainer, StyledBlackInformationTypography, StyledDarkRedInformationTypography } from "../../../../components/commonComponents/StyledComponents/styled";
import { ArrowDropDown, ArrowDropUp, Delete, Share } from "@mui/icons-material";
import ActionButton from "../../../../components/commonComponents/Buttons/ActionButton";
import ShareModal from "./ShareModal";
import { resetReportShareData } from "../../../../api_calls/campaignSheet";
import { fetchListOfSharedUsers, resetShareModalInfo, revokeSharedAccessFromUser } from "../../../../api_calls/ReportSettings";
import {
    ExtraPaddingLeftStyledCell,
    LeftStyledTableCell,
    LISTING_ACTION_TO_TABLE_GAP,
    StyledTableCell,
} from "../../../../components/commonComponents/TabularLists/styles";
import { useSelector } from "react-redux";
import { formatDate, generateRandomNumber } from "../../../../api_calls/utils";
import { checkUserDetailsFromCookie } from "../../../../api_calls/userManagement";
import { canEditAcrossCompanies } from "../../../../api_calls/crossCompanyAccess";
import Confirmation from "../../../../components/commonComponents/Confirmation";

const ShareReport = ({ campaignRecord }) => {
    const sharedUsers = useSelector((state) => state.userSelections);
    const [showReportShare, setShowReportShare] = React.useState(false);
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [userToDelete, setUserToDelete] = React.useState("");
    // sorting measures for files listing
    const [sortingMeasure, setSortingMeasure] = React.useState({
        email: "asc",
        invited_by: "asc",
        shared_on: "asc",
    });
    const [sortingParamName, setSortingParamName] = React.useState("email");
    const loggedInUserDetail = checkUserDetailsFromCookie();
    let userDetailObj = {};
    if (loggedInUserDetail.success === true) {
        userDetailObj = loggedInUserDetail.user_detail;
    }

    // Tenant shares and deletes only a campaign their company owns. Cyreen still shares across companies.
    const userType = typeof userDetailObj.user_type === "string" ? userDetailObj.user_type.toLowerCase() : "";
    const ownsCampaign = userDetailObj.assigned_company?.id === campaignRecord.owner_company_id;
    const canAccessShareAndDelete =
        (canEditAcrossCompanies(userType) === true && userType !== "tenant")
        || (ownsCampaign === true && userType !== "cyreen");

    const sortedResults = (toBeSortedList) => {
        return toBeSortedList.slice().sort((a, b) => {
            if (sortingParamName === "invited_by") {
                return sortingMeasure.invited_by === "asc"
                    ? a.invited_by.localeCompare(b.invited_by)
                    : b.invited_by.localeCompare(a.invited_by);
            } else if (sortingParamName === "email") {
                return sortingMeasure.email === "asc" ? a.email.localeCompare(b.email) : b.email.localeCompare(a.email);
            } else if (sortingParamName === "shared_on") {
                return sortingMeasure.shared_on === "asc"
                    ? new Date(a.shared_on) - new Date(b.shared_on)
                    : new Date(b.shared_on) - new Date(a.shared_on);
            } else {
                return sortingMeasure.email === "asc"
                    ? a.email.localeCompare(b.email)
                    : b.email.localeCompare(a.email);
            }
        });
    };

    let sharedUserListitems =
        typeof sharedUsers.shared_report_users_list !== "undefined" && sharedUsers.shared_report_users_list.length > 0 ? sortedResults(sharedUsers.shared_report_users_list) : [];

    const handleSortingParamChange = (paramName) => {
        setSortingMeasure((prevState) => {
            return {
                ...prevState,
                [paramName]: prevState[paramName] === "asc" ? "desc" : "asc",
            };
        });
        setSortingParamName(paramName);
    };

    React.useEffect(() => {
        fetchListOfSharedUsers(campaignRecord.campaign_id);
    }, []);

    return (
        <Box>
            <Box height={10} />
            <Typography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal", marginTop: "5px" }}>
                You can share the report with brands. Reports shared with brands are filtered according to the settings defined in the <i>Report Filters</i> section. You can add or remove brands at any time.
            </Typography>
            {/* - share report is only available if current user company == campaign owner company - */}
            {
                canAccessShareAndDelete === true && (
                    <ActionButton
                        onClick={(e) => {
                            e.preventDefault();
                            setShowReportShare(true);
                        }}
                        label={"Share Report"}
                        icon={<Share />}
                        sx={{ mt: 2, mb: 0 }}
                    />
                )
            }
            {
                canAccessShareAndDelete === false && (
                    <StyledDarkRedInformationTypography variant="body2" sx={{ marginTop: 2, fontWeight: "bold" }}>
                        You cannot share campaigns that were not created by your company.
                    </StyledDarkRedInformationTypography>
                )
            }
            <Box height={LISTING_ACTION_TO_TABLE_GAP} />
            <TableContainer component={Paper}>
                <Table aria-label="customized table">
                    {/* ------ header row ------ */}
                    <TableHead>
                        <TableRow>
                            <ExtraPaddingLeftStyledCell align="left" className="text-wrapper-store_list" sx={{ padding: "5px", paddingLeft: "0px", fontSize: "13px" }}>
                                <Stack direction={"row"} spacing={1}>
                                    <div>
                                        E-mail
                                    </div>
                                    {sortingMeasure.email === "asc" ? (
                                        <ArrowDropDown
                                            sx={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                                handleSortingParamChange("email");
                                            }}
                                        />
                                    ) : (
                                        <ArrowDropUp
                                            sx={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                                handleSortingParamChange("email");
                                            }}
                                        />
                                    )}
                                </Stack>
                            </ExtraPaddingLeftStyledCell>
                            {/* ----- Shared by ------ */}
                            <LeftStyledTableCell align="left" className="text-wrapper-store_list" sx={{ padding: "5px", paddingLeft: "0px", fontSize: "13px" }}>
                                <Stack direction={"row"} spacing={1}>
                                    <div>
                                        Shared By
                                    </div>
                                    {sortingMeasure.invited_by === "asc" ? (
                                        <ArrowDropDown
                                            sx={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                                handleSortingParamChange("invited_by");
                                            }}
                                        />
                                    ) : (
                                        <ArrowDropUp
                                            sx={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                                handleSortingParamChange("invited_by");
                                            }}
                                        />
                                    )}
                                </Stack>
                            </LeftStyledTableCell>
                            {/* ---------- End Date ---------- */}
                            <LeftStyledTableCell align="left" className="text-wrapper-store_list" sx={{ padding: "5px", paddingLeft: "0px", fontSize: "13px" }}>
                                <Stack direction={"row"} spacing={1}>
                                    <div>
                                        Shared On
                                    </div>
                                    {sortingMeasure.shared_on === "asc" ? (
                                        <ArrowDropDown
                                            sx={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                                handleSortingParamChange("shared_on");
                                            }}
                                        />
                                    ) : (
                                        <ArrowDropUp
                                            sx={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                                handleSortingParamChange("shared_on");
                                            }}
                                        />
                                    )}
                                </Stack>
                            </LeftStyledTableCell>
                            {/* -----------Action-------- */}
                            {
                                canAccessShareAndDelete === true && (
                                    <LeftStyledTableCell align="left" className="text-wrapper-store_list" sx={{ padding: "5px", paddingLeft: "0px", fontSize: "13px" }}>
                                        Action
                                    </LeftStyledTableCell>
                                )
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            sharedUserListitems.length > 0 ? (
                                sharedUserListitems.map((sharedObj, index) => (
                                    <TableRow key={"shared_user_row_" + index}>
                                        <ExtraPaddingLeftStyledCell
                                            component="td"
                                            scope="row"
                                            className="text-wrapper-store_list"
                                            sx={{ padding: "4px", paddingLeft: "0px" }}
                                        >
                                            {sharedObj.email}
                                        </ExtraPaddingLeftStyledCell>
                                        {/* --------- Email ---------- */}
                                        <StyledTableCell component="td" scope="row" className="text-wrapper-store_list wrapLargeText" sx={{ padding: "4px", paddingLeft: "0px" }}>
                                            {sharedObj.invited_by}
                                        </StyledTableCell>
                                        {/* ---------- date_end --------- */}
                                        <StyledTableCell component="td" scope="row" className="text-wrapper-store_list" sx={{ padding: "4px", paddingLeft: "0px" }}>
                                            {formatDate(sharedObj.shared_on)}
                                        </StyledTableCell>
                                        {/* ------------ Action  --------- */}
                                        {
                                            canAccessShareAndDelete === true && (
                                                <StyledTableCell component="td" scope="row" className="text-wrapper-store_list" sx={{ padding: "4px", paddingLeft: "0px" }}>
                                                    <HtmlTooltip title="Remove User">
                                                        <IconContainer>
                                                            <Delete
                                                                fontSize="small"
                                                                sx={{ color: "#AF3241", marginTop: "5px", cursor: "pointer" }}
                                                                onClick={(e) => {
                                                                    setUserToDelete(sharedObj.email);
                                                                    setShowConfirmation(true);
                                                                }}
                                                            />
                                                        </IconContainer>
                                                    </HtmlTooltip>
                                                </StyledTableCell>
                                            )
                                        }
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <ExtraPaddingLeftStyledCell component="td" scope="row" className="text-wrapper-store_list" colSpan={6} sx={{ padding: "5px", }}>
                                        No results
                                    </ExtraPaddingLeftStyledCell>
                                </TableRow>
                            )
                        }

                    </TableBody>
                </Table>
            </TableContainer>

            {showReportShare === true && (
                <ShareModal
                    open={showReportShare}
                    onClose={() => {
                        setShowReportShare(false);
                        resetShareModalInfo();
                        fetchListOfSharedUsers(campaignRecord.campaign_id);
                    }}
                    size={"md"}
                    closeModal={() => {
                        setShowReportShare(false);
                        resetShareModalInfo();
                        fetchListOfSharedUsers(campaignRecord.campaign_id);
                    }}
                    usedFor={"update"}
                    campaignRecord={campaignRecord}
                />
            )}
            {/* ---------- delete Confirmation ---------- */}
            {showConfirmation === true && (
                <Confirmation
                    confirmationActionCall={() => {
                        revokeSharedAccessFromUser(campaignRecord.campaign_id, userToDelete).then((info) => {
                            if (info.success === true) {
                                setUserToDelete("");
                                setShowConfirmation(false);
                            }
                        });
                    }}
                    closePopup={(e) => {
                        setUserToDelete("");
                        setShowConfirmation(false);
                    }}
                    showConfirmationPopup={showConfirmation}
                    confirmationTitle={"Revoke Access"}
                    confirmationMessage={
                        <>
                            <Typography variant="body2">
                                You are about to revoke this user’s access to the campaign report. This action cannot be undone. You can re-share the report at any time.
                            </Typography>
                            <Box height={20} />
                            <Typography variant="body2">Are you sure you want to proceed?</Typography>
                        </>
                    }
                />
            )}
        </Box>
    );
};

export default ShareReport;
